import { DataGrid } from '@components/dataGrid/Datagrid';
import { DocumentStatus } from '@models/document';
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { useSearchDocuments } from '@services/document';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { Box, Grid, Typography } from '@mui/material';
import { format } from 'date-fns-tz';
import { useCallback, useMemo } from 'react';
import { Checkbox } from '@components/checkbox';
import { NoResultsOverlay } from '@components/dataGrid/common/NoResultsOverlay';
import { DocumentSearchResponse } from '@lio/api-types';
import { LongDateFormatWithoutSeconds, getDateFormatter } from '@utils/date';

import { ClockFormat } from '@lio/rule-engine';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

const getPolicyDetailsDocumentsColumns = ({ clockFormat }: { clockFormat: ClockFormat }): GridColDef[] => [
  {
    field: 'fileNameType',
    headerName: 'File Name/Type',
    flex: 2,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant='bodySmall'>{params.row.fileName}</Typography>
          <Typography
            variant='bodySmall'
            sx={{
              color: 'info.hint',
            }}
          >
            {format(new Date(params.row.createdDate), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds))}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 2,
    sortable: false,
    valueGetter: (value, row) => row.description ?? '-',
  },
];

type IncludeDocumentsProps = {
  quoteId?: string;
  setIncludedDocuments?: (documents: DocumentSearchResponse['items']) => void;
};

export const IncludeDocuments = ({ quoteId, setIncludedDocuments }: IncludeDocumentsProps) => {
  const { documents, isPending } = useSearchDocuments({
    quoteId,
    documentGenerationStatus: DocumentStatus.Completed,
    pageLimit: 1000,
  });
  const totalItemCount = documents?.pagination?.total ?? 0;
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const noDocumentsOverlay = useCallback(
    () => <NoResultsOverlay noResultsMsg={{ subtitle: 'No documents to display' }} />,
    [],
  );

  const allFiles = documents?.items.flatMap((doc) => ({
    id: doc.id,
    fileName: doc.file?.fileName,
    fileType: doc.file?.fileType,
    createdDate: doc.createdAt,
    size: '5',
    description: doc.file?.description,
  }));

  const handleRowSelectionModelChange = (selectedRowIds: GridRowSelectionModel) => {
    if (selectedRowIds.length > 0) {
      const selectedDocuments = documents?.items.filter((doc) => selectedRowIds.includes(doc.id));
      setIncludedDocuments?.(selectedDocuments ?? []);
    } else {
      setIncludedDocuments?.([]);
    }
  };

  const documentsColumns = useMemo(() => {
    return getPolicyDetailsDocumentsColumns({ clockFormat });
  }, [clockFormat]);

  return (
    <Grid
      size={{ xs: 11 }}
      sx={{
        m: 2,
      }}
    >
      <Typography
        variant='h5'
        sx={{
          marginBottom: 2,
        }}
      >
        Included Document(s)
      </Typography>
      <Box sx={{ height: '175px' }}>
        <DataGrid
          hideFooter
          columns={documentsColumns}
          rows={allFiles}
          rowCount={totalItemCount ?? 0}
          initialState={{ density: 'compact' }}
          columnHeaderHeight={40}
          rowHeight={100}
          loading={isPending}
          sx={styles.dataGrid}
          checkboxSelection
          onRowSelectionModelChange={handleRowSelectionModelChange}
          slots={{ baseCheckbox: Checkbox, noRowsOverlay: noDocumentsOverlay, noResultsOverlay: noDocumentsOverlay }}
        />
      </Box>
    </Grid>
  );
};
